import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, LoadingSkeleton } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Sparkles, Table2, Loader2, LifeBuoy } from "lucide-react";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fallback;
};

const B2CPlan = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateCardPlan, setRateCardPlan] = useState(null); // {id, name} whose card is open
  const [rateCard, setRateCard] = useState(null);
  const [rateCardLoading, setRateCardLoading] = useState(false);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/b2c/seller/plan`);
      setData(res.data);
    } catch (e) {
      toast.error(errDetail(e, "Could not load your plan"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const openRateCard = async (plan) => {
    setRateCardPlan({ id: plan.id, name: plan.name });
    setRateCard(null);
    setRateCardLoading(true);
    try {
      const res = await axios.get(`${API}/b2c/seller/plan/${plan.id}/rate-card`);
      setRateCard(res.data);
    } catch (e) {
      toast.error(errDetail(e, "Could not load the rate card"));
    } finally {
      setRateCardLoading(false);
    }
  };

  const current = data?.current_plan;
  const zones = rateCard?.zones || ["A", "B", "C", "D", "E", "F"];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader title="Plan" subtitle="Your assigned B2C plan and rate card." />

        {loading ? (
          <LoadingSkeleton variant="card" rows={3} />
        ) : !current ? (
          /* No plan mapped to this seller → contact Edgistify */
          <Card className="border-0 shadow-sm" data-testid="no-plan-state">
            <CardContent className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-bg">
                <LifeBuoy className="h-7 w-7 text-brand" />
              </div>
              <h2 className="text-lg font-heading font-bold text-slate-800">No plan assigned yet</h2>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-slate-500">
                Your account doesn't have a B2C plan mapped. Kindly Contact Edgistify Support/Sales Team.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="border-0 shadow-sm sm:col-span-2 bg-brand/5">
              <CardContent className="p-5">
                <div className="mb-1 flex items-center gap-2 text-xs font-medium text-brand">
                  <Sparkles className="h-3.5 w-3.5" /> YOUR PLAN
                </div>
                <div className="text-2xl font-heading font-bold text-slate-800">{current.name}</div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span>Monthly fee: <span className="font-semibold tabular-nums">{inr(current.monthly_fee)}</span></span>
                  {current.cod_cycle_days != null && (
                    <span>COD payout: <span className="font-semibold tabular-nums">{current.cod_cycle_days} days</span></span>
                  )}
                  {current.fee_refund_at_shipments != null && (
                    <span>Fee refunded at <span className="font-semibold tabular-nums">{current.fee_refund_at_shipments}</span> shipments/mo</span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => openRateCard(current)}
                  data-testid="view-current-rate-card"
                >
                  <Table2 className="mr-1.5 h-3.5 w-3.5" /> View rate card
                </Button>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">
                  <Wallet className="h-3.5 w-3.5" /> WALLET BALANCE
                </div>
                <div className={`text-2xl font-heading font-bold tabular-nums ${Number(data?.wallet_balance) < 0 ? "text-red-600" : "text-slate-800"}`}>
                  {inr(data?.wallet_balance)}
                </div>
                <div className="mt-1 text-xs capitalize text-slate-500">{data?.billing_mode || "prepaid"} billing</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={!!rateCardPlan} onOpenChange={(v) => !v && setRateCardPlan(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{rateCardPlan?.name} — rate card</DialogTitle>
            <DialogDescription>Your applicable shipping rates on this plan (₹ per zone, base slab). COD is flat + % of order value.</DialogDescription>
          </DialogHeader>
          {rateCardLoading ? (
            <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
          ) : !rateCard?.rate_card?.length ? (
            <div className="py-10 text-center text-sm text-slate-500">No rates configured on this plan yet.</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200" data-testid="rate-card-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-xs text-slate-600">
                    <th className="px-3 py-2 font-semibold">Courier</th>
                    <th className="px-3 py-2 font-semibold">Mode</th>
                    <th className="px-3 py-2 font-semibold text-right">Slab</th>
                    {zones.map((z) => (
                      <th key={z} className="px-3 py-2 font-semibold text-right">{z}</th>
                    ))}
                    <th className="px-3 py-2 font-semibold text-right">COD</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rateCard.rate_card.map((r, i) => (
                    <tr key={`${r.courier_code}-${r.mode}-${r.slab_kg}-${i}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800">{r.courier_name}</td>
                      <td className="px-3 py-2 capitalize text-slate-600">{r.mode}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.slab_kg} kg</td>
                      {zones.map((z) => (
                        <td key={z} className="px-3 py-2 text-right tabular-nums text-slate-700">
                          {r.fwd?.[z] != null ? `₹${r.fwd[z]}` : <span className="text-slate-300">—</span>}
                        </td>
                      ))}
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-600">
                        ₹{r.cod_flat ?? 0} + {r.cod_pct ?? 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default B2CPlan;
